import { FC, useEffect } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router';
import { BrandScreen } from '../../components/Brand';
import { colors } from '../../theme/tokens';

// Error screens (`/404`, `/500` and any unknown route), without
// a design in the XD (DESIGN.md, gap 3). A single screen parameterised by
// code: it replaces pages/NotFound and pages/InternalError, which were the
// same file twice.
//
// Own design: the Login brand screen (components/Brand/BrandScreen)
// with the code large in serif (`h1`, the XD `.titre-app`) in light
// orange, the message in French below and "Retour à l'accueil" as the
// primary orange button towards "/", which leads to the Home with a session and to
// Login without one. No header nor navigation: the screen does not know whether there is a session.

export type ErrorCode = 404 | 500;

const MESSAGES: Record<ErrorCode, { title: string; message: string }> = {
  404: {
    title: 'Page introuvable',
    message: "La page demandée n'existe pas ou a été déplacée.",
  },
  500: {
    title: 'Une erreur est survenue',
    message: 'Le service a rencontré un problème. Veuillez réessayer dans quelques instants.',
  },
};

const ErrorScreen: FC<{ code: ErrorCode }> = ({ code }) => {
  const { title, message } = MESSAGES[code];

  useEffect(() => {
    document.title = `ArchivOs - ${title}`;
  }, [title]);

  return (
    <BrandScreen>
      <Box
        data-testid={`error-${code}`}
        sx={{ mt: 5, display: 'flex', flexDirection: 'column', gap: 1.5 }}
      >
        <Typography
          component="p"
          variant="h1"
          aria-hidden
          sx={{ color: colors.accentLight, fontSize: '5rem', lineHeight: 1 }}
        >
          {code}
        </Typography>
        <Typography component="h2" variant="h2" sx={{ color: colors.white }}>
          {title}
        </Typography>
        <Typography sx={{ color: colors.white, opacity: 0.85 }}>
          {message}
        </Typography>
        <Button
          component={RouterLink}
          to="/"
          variant="contained"
          color="secondary"
          size="large"
          sx={{ mt: 2, alignSelf: 'flex-start' }}
        >
          Retour à l'accueil
        </Button>
      </Box>
    </BrandScreen>
  );
};

export default ErrorScreen;
